import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, CalendarDays, BedDouble, Sparkles, LogOut, Calendar, TrendingUp,
  MessageSquare, Link2, Tag, Brush, FileText, Settings as SettingsIcon, Users, UserCircle,
  Menu, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PropertySwitcher from "../components/app/PropertySwitcher";
import ChangePasswordModal from "../components/app/ChangePasswordModal";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview", perm: "dashboard.view" },
  { to: "/dashboard/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar", perm: "calendar.view" },
  { to: "/dashboard/reservations", label: "Reservations", icon: CalendarDays, testid: "nav-reservations", perm: "reservations.view" },
  { to: "/dashboard/guests", label: "Guests CRM", icon: UserCircle, testid: "nav-guests", perm: "guests.view" },
  { to: "/dashboard/rooms", label: "Rooms", icon: BedDouble, testid: "nav-rooms", perm: "rooms.view" },
  { to: "/dashboard/housekeeping", label: "Housekeeping", icon: Brush, testid: "nav-housekeeping", perm: "housekeeping.view" },
  { to: "/dashboard/channels", label: "Channels", icon: Link2, testid: "nav-channels", perm: "channels.view" },
  { to: "/dashboard/rate-plans", label: "Rate Plans", icon: Tag, testid: "nav-rate-plans", perm: "rate_plans.view" },
  { to: "/dashboard/rate-shopper", label: "Rate Shopper", icon: TrendingUp, testid: "nav-rate-shopper", perm: "rate_shopper.view" },
  { to: "/dashboard/reviews", label: "Reviews", icon: MessageSquare, testid: "nav-reviews", perm: "reviews.view" },
  { to: "/dashboard/revenue-ai", label: "Revenue AI", icon: Sparkles, testid: "nav-revenue-ai", perm: "revenue_ai.view" },
  { to: "/dashboard/reports", label: "Reports", icon: FileText, testid: "nav-reports", perm: "reports.view" },
  { to: "/dashboard/team", label: "Team", icon: Users, testid: "nav-team", perm: "team.view" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", perm: "settings.view" },
];

const ROLE_LABELS = {
  hotel_admin: "Hotel Admin", property_manager: "Property Manager", front_desk: "Front Desk",
  housekeeping_supervisor: "HK Supervisor", housekeeping_staff: "HK Staff",
  revenue_manager: "Revenue Manager", accountant: "Accountant", super_admin: "Super Admin",
};

const ROLE_PERMS = {
  super_admin: ["*"], hotel_admin: ["*"],
  property_manager: ["dashboard.view", "calendar.view", "reservations.*", "rooms.*", "housekeeping.*", "channels.*", "rate_plans.*", "rate_shopper.view", "reviews.*", "revenue_ai.*", "guests.*", "reports.view"],
  front_desk: ["dashboard.view", "calendar.view", "reservations.*", "rooms.view", "guests.view"],
  housekeeping_supervisor: ["dashboard.view", "housekeeping.*", "rooms.view", "guests.view"],
  housekeeping_staff: ["housekeeping.view"],
  revenue_manager: ["dashboard.view", "rate_plans.*", "rate_shopper.*", "revenue_ai.*", "channels.*", "reviews.view", "reports.view"],
  accountant: ["dashboard.view", "reports.*", "reservations.view", "guests.view"],
};

function hasPerm(role, key) {
  const perms = ROLE_PERMS[role] || [];
  if (perms.includes("*")) return true;
  if (perms.includes(key)) return true;
  const prefix = key.split(".")[0] + ".*";
  return perms.includes(prefix);
}

function firstAllowedPath(role) {
  if (hasPerm(role, "dashboard.view")) return "/dashboard";
  const priority = [
    ["housekeeping.view", "/dashboard/housekeeping"],
    ["reservations.view", "/dashboard/reservations"],
    ["rooms.view", "/dashboard/rooms"],
    ["guests.view", "/dashboard/guests"],
    ["rate_plans.view", "/dashboard/rate-plans"],
    ["reports.view", "/dashboard/reports"],
  ];
  for (const [p, path] of priority) {
    if (hasPerm(role, p)) return path;
  }
  return "/dashboard";
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  async function handleLogout() {
    await logout();
    toast.success("Signed out");
    navigate("/");
  }

  const role = user?.role || "hotel_admin";
  const extendedNav = NAV.filter((n) => {
    if (n.perm === "team.view") return role === "hotel_admin" || role === "super_admin";
    if (n.perm === "settings.view") return role === "hotel_admin" || role === "super_admin";
    return hasPerm(role, n.perm);
  });

  if (location.pathname === "/dashboard" && !hasPerm(role, "dashboard.view")) {
    const dest = firstAllowedPath(role);
    if (dest !== "/dashboard") return <Navigate to={dest} replace />;
  }

  const mustChange = !!user?.must_change_password;

  return (
    <div className="min-h-[100dvh] bg-[#0B101E] text-[#F8F9FA] flex" data-testid="dashboard-layout">
      <SidebarContent extendedNav={extendedNav} role={role} user={user} onLogout={handleLogout} />

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black/60 z-40 md:hidden" data-testid="mobile-backdrop" />
          <aside className="fixed top-0 left-0 h-[100dvh] w-[80%] max-w-xs z-50 border-r border-white/5 bg-[#0a0f1c] md:hidden flex flex-col" data-testid="mobile-drawer" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="px-6 h-16 flex items-center justify-between border-b border-white/5">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="Booking4U" className="h-8 w-8 object-contain" />
                <span className="font-display text-xl">Booking4U</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-white/60 hover:text-white p-2 -mr-2" aria-label="Close menu" data-testid="mobile-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-white/5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Active property</div>
              <PropertySwitcher />
            </div>
            <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
              {extendedNav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition min-h-[44px] ${isActive ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/65 hover:text-white hover:bg-white/5"}`}>
                  <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-white/5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <div className="text-xs text-white/40 mb-1">Signed in as</div>
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-white/40 truncate">{user?.email}</div>
              <div className="mt-2 inline-block text-[10px] tracking-widest uppercase px-2 py-1 rounded border bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/25">{ROLE_LABELS[role] || role}</div>
              <button onClick={handleLogout} className="mt-3 w-full text-sm flex items-center gap-2 text-white/60 hover:text-[#D4AF37] min-h-[44px]">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>
        </>
      )}

      <div className="flex-1 min-w-0">
        {/* Mobile header with hamburger */}
        <header className="md:hidden sticky top-0 z-30 h-14 border-b border-white/5 bg-[#0a0f1c] flex items-center justify-between px-4" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <button onClick={() => setMobileOpen(true)} className="w-11 h-11 flex items-center justify-center text-white/80 hover:text-[#D4AF37]" aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Booking4U" className="h-7 w-7 object-contain" />
            <span className="font-display text-lg">Booking4U</span>
          </div>
          <button onClick={handleLogout} className="text-white/60 text-sm px-2">Sign out</button>
        </header>
        <main className="p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>

      {mustChange && <ChangePasswordModal />}
    </div>
  );
}

function SidebarContent({ extendedNav, role, user, onLogout }) {
  return (
    <aside className="w-64 shrink-0 border-r border-white/5 bg-[#0a0f1c] hidden md:flex flex-col sticky top-0 h-[100dvh]">
      <div className="px-6 h-16 flex items-center gap-2 border-b border-white/5">
        <img src="/logo.png" alt="Booking4U" className="h-8 w-8 object-contain" />
        <span className="font-display text-xl">Booking4U</span>
      </div>
      <div className="p-4 border-b border-white/5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Active property</div>
        <PropertySwitcher />
      </div>
      <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
        {extendedNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${isActive ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/65 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/5">
        <div className="text-xs text-white/40 mb-1">Signed in as</div>
        <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs text-white/40 truncate">{user?.email}</div>
        <div className="mt-2 inline-block text-[10px] tracking-widest uppercase px-2 py-1 rounded border bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/25" data-testid="user-role-badge">{ROLE_LABELS[role] || role}</div>
        <button onClick={onLogout} className="mt-3 w-full text-sm flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition" data-testid="logout-button">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}
